if (!customElements.get('kiwibit-slideshow')) {
  class KiwibitSlideshow extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.slides = Array.from(this.querySelectorAll('[data-kiwibit-slide]'));
      this.paginationButtons = Array.from(this.querySelectorAll('[data-kiwibit-pagination]'));
      this.progressBars = this.paginationButtons.map((button) => button.querySelector('[data-kiwibit-progress]'));
      this.prevButton = this.querySelector('[data-kiwibit-prev]');
      this.nextButton = this.querySelector('[data-kiwibit-next]');
      this.autoplaySpeed = Number.parseInt(this.dataset.autoplaySpeed, 10) || 5000;
      this.isDesignMode = Boolean(window.Shopify?.designMode);
      this.activeIndex = Math.max(
        0,
        this.slides.findIndex((slide) => slide.classList.contains('is-active'))
      );
      this.frameId = null;
      this.activeVideo = null;
      this.activeVideoMount = null;
      this.activeVideoCleanup = null;
      this.cancelPosterWait = null;
      this.cancelVideoFrameWait = null;
      this.playbackGeneration = 0;
      this.navigationGeneration = 0;
      this.mediaMode = 'image';
      const initialRect = this.getBoundingClientRect();
      this.isInViewport = initialRect.bottom > 0 && initialRect.top < window.innerHeight;
      this.initialized = true;

      this.handlePrevClick = this.handlePrevClick.bind(this);
      this.handleNextClick = this.handleNextClick.bind(this);
      this.handlePaginationClick = this.handlePaginationClick.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleTouchMove = this.handleTouchMove.bind(this);
      this.handleTouchEnd = this.handleTouchEnd.bind(this);

      this.prevButton?.addEventListener('click', this.handlePrevClick);
      this.nextButton?.addEventListener('click', this.handleNextClick);
      this.paginationButtons.forEach((button) => button.addEventListener('click', this.handlePaginationClick));
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
      window.addEventListener('resize', this.handleResize);
      this.addEventListener('touchstart', this.handleTouchStart, { passive: true });
      this.addEventListener('touchmove', this.handleTouchMove, { passive: true });
      this.addEventListener('touchend', this.handleTouchEnd);

      this.setupViewportObserver();
      this.goToSlide(this.activeIndex, { autoplay: !this.isDesignMode, restart: !this.isDesignMode });
    }

    disconnectedCallback() {
      this.prevButton?.removeEventListener('click', this.handlePrevClick);
      this.nextButton?.removeEventListener('click', this.handleNextClick);
      this.paginationButtons.forEach((button) => button.removeEventListener('click', this.handlePaginationClick));
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      window.removeEventListener('resize', this.handleResize);
      this.removeEventListener('touchstart', this.handleTouchStart);
      this.removeEventListener('touchmove', this.handleTouchMove);
      this.removeEventListener('touchend', this.handleTouchEnd);
      window.clearTimeout(this.resizeTimer);
      this.viewportObserver?.disconnect();
      this.viewportObserver = null;
      this.navigationGeneration += 1;
      this.stopPlayback();
    }

    handlePrevClick() {
      this.goToSlide(this.activeIndex - 1);
    }

    handleNextClick() {
      this.goToSlide(this.activeIndex + 1);
    }

    handlePaginationClick(event) {
      const index = Number.parseInt(event.currentTarget.dataset.index, 10);
      if (Number.isNaN(index)) return;
      this.goToSlide(index);
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.stopPlayback();
        return;
      }

      if (!this.isDesignMode && this.isInViewport) {
        this.startPlayback();
      }
    }

    handleResize() {
      if (this.isDesignMode || document.hidden || !this.isInViewport) return;

      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => this.startPlayback(), 120);
    }

    handleBlockSelect(event) {
      const slide = event.target?.closest?.('[data-kiwibit-slide]');
      if (!slide || !this.contains(slide)) return;

      const index = Number.parseInt(slide.dataset.index, 10);
      if (Number.isNaN(index)) return;

      this.goToSlide(index, { autoplay: false });
    }

    handleTouchStart(event) {
      if (!window.matchMedia('(max-width: 749px)').matches) return;
      const touch = event.touches?.[0];
      if (!touch) return;

      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
      this.touchDeltaX = 0;
      this.touchDeltaY = 0;
    }

    handleTouchMove(event) {
      if (this.touchStartX == null || !window.matchMedia('(max-width: 749px)').matches) return;
      const touch = event.touches?.[0];
      if (!touch) return;

      this.touchDeltaX = touch.clientX - this.touchStartX;
      this.touchDeltaY = touch.clientY - this.touchStartY;
    }

    handleTouchEnd() {
      if (this.touchStartX == null || !window.matchMedia('(max-width: 749px)').matches) return;

      const absX = Math.abs(this.touchDeltaX || 0);
      const absY = Math.abs(this.touchDeltaY || 0);

      if (absX > 48 && absX > absY) {
        if (this.touchDeltaX < 0) {
          this.goToSlide(this.activeIndex + 1);
        } else {
          this.goToSlide(this.activeIndex - 1);
        }
      }

      this.touchStartX = null;
      this.touchStartY = null;
      this.touchDeltaX = 0;
      this.touchDeltaY = 0;
    }

    setupViewportObserver() {
      if (!('IntersectionObserver' in window)) {
        this.isInViewport = true;
        return;
      }

      this.viewportObserver = new IntersectionObserver(
        ([entry]) => {
          const wasInViewport = this.isInViewport;
          this.isInViewport = Boolean(entry?.isIntersecting);
          if (this.isInViewport === wasInViewport) return;

          if (this.isInViewport && !document.hidden && !this.isDesignMode) {
            this.startPlayback();
          } else if (!this.isInViewport) {
            this.stopPlayback();
          }
        },
        { threshold: 0.01 }
      );
      this.viewportObserver.observe(this);
    }

    goToSlide(index, options = {}) {
      if (!this.slides.length) return;

      const normalizedIndex = ((index % this.slides.length) + this.slides.length) % this.slides.length;
      const navigationGeneration = ++this.navigationGeneration;
      const targetSlide = this.slides[normalizedIndex];

      this.stopPlayback();
      this.waitForPoster(targetSlide).then(() => {
        if (navigationGeneration !== this.navigationGeneration || !this.isConnected) return;
        this.commitSlide(normalizedIndex, options);
      });
    }

    commitSlide(index, options = {}) {
      const normalizedIndex = ((index % this.slides.length) + this.slides.length) % this.slides.length;
      this.activeIndex = normalizedIndex;

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === normalizedIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        slide.tabIndex = isActive ? 0 : -1;
      });

      this.paginationButtons.forEach((button, buttonIndex) => {
        button.classList.toggle('is-active', buttonIndex === normalizedIndex);
      });

      this.progressBars.forEach((bar) => {
        if (bar) bar.style.transform = 'scaleX(0)';
      });

      const shouldAutoplay = options.autoplay ?? !this.isDesignMode;

      if (shouldAutoplay && !document.hidden && this.isInViewport) {
        this.startPlayback();
      }
    }

    async startPlayback() {
      if (!this.slides.length || document.hidden || this.isDesignMode || !this.isInViewport) return;

      this.stopPlayback();
      const playbackGeneration = this.playbackGeneration;
      const activeSlide = this.slides[this.activeIndex];
      const videoTemplate = this.getActiveVideoTemplate(activeSlide);

      if (!videoTemplate) {
        this.mediaMode = 'image';
        if (this.slides.length > 1) {
          this.startImageTimer(playbackGeneration);
        }
        return;
      }

      const posterReady = await this.waitForPoster(activeSlide);
      if (!posterReady || !this.isPlaybackCurrent(playbackGeneration, activeSlide)) {
        if (this.isPlaybackCurrent(playbackGeneration, activeSlide) && this.slides.length > 1) {
          this.mediaMode = 'image';
          this.startImageTimer(playbackGeneration);
        }
        return;
      }

      const activeVideo = this.mountVideo(activeSlide, videoTemplate);
      if (!activeVideo || !this.isPlaybackCurrent(playbackGeneration, activeSlide)) return;

      this.mediaMode = 'video';
      activeVideo.loop = this.slides.length === 1;
      activeVideo.currentTime = 0;
      this.attachActiveVideoState(activeVideo, activeSlide, playbackGeneration);

      if (!activeVideo.loop) {
        activeVideo.onended = () => {
          if (this.isPlaybackCurrent(playbackGeneration, activeSlide)) {
            this.goToSlide(this.activeIndex + 1);
          }
        };
      }

      try {
        await this.playVideoWithTimeout(activeVideo, 10000);
        if (!this.isPlaybackCurrent(playbackGeneration, activeSlide) || this.activeVideo !== activeVideo) {
          if (this.activeVideo === activeVideo) {
            this.teardownActiveVideo();
          }
          return;
        }

        this.revealVideoAfterFrame(activeVideo, activeSlide, playbackGeneration);
        if (!activeVideo.loop) {
          this.trackVideoProgress(activeVideo, playbackGeneration);
        }
      } catch (error) {
        if (!this.isPlaybackCurrent(playbackGeneration, activeSlide)) return;

        this.teardownActiveVideo();
        this.mediaMode = 'image';
        if (this.slides.length > 1) {
          this.startImageTimer(playbackGeneration);
        }
      }
    }

    stopPlayback() {
      this.playbackGeneration += 1;
      this.cancelPosterWait?.();
      this.cancelPosterWait = null;
      this.cancelVideoFrameWait?.();
      this.cancelVideoFrameWait = null;

      if (this.frameId) {
        window.cancelAnimationFrame(this.frameId);
        this.frameId = null;
      }

      this.teardownActiveVideo();
    }

    startImageTimer(playbackGeneration = this.playbackGeneration) {
      const progressBar = this.progressBars[this.activeIndex];
      if (!progressBar) return;

      const startTime = performance.now();

      const tick = (now) => {
        if (playbackGeneration !== this.playbackGeneration || document.hidden) return;

        const progress = Math.min((now - startTime) / this.autoplaySpeed, 1);
        progressBar.style.transform = `scaleX(${progress})`;

        if (progress >= 1) {
          this.goToSlide(this.activeIndex + 1);
          return;
        }

        this.frameId = window.requestAnimationFrame(tick);
      };

      this.frameId = window.requestAnimationFrame(tick);
    }

    trackVideoProgress(video, playbackGeneration) {
      const progressBar = this.progressBars[this.activeIndex];
      if (!progressBar) return;

      const tick = () => {
        if (
          this.activeVideo !== video
          || playbackGeneration !== this.playbackGeneration
          || document.hidden
        ) return;

        const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 0;
        const progress = duration ? Math.min(video.currentTime / duration, 1) : 0;
        progressBar.style.transform = `scaleX(${progress})`;

        if (!video.paused && !video.ended) {
          this.frameId = window.requestAnimationFrame(tick);
        }
      };

      this.frameId = window.requestAnimationFrame(tick);
    }

    waitForPoster(slide) {
      const poster = this.mountPoster(slide);
      if (!poster) return Promise.resolve(false);
      if (poster.complete) return this.decodePoster(poster);

      this.cancelPosterWait?.();

      return new Promise((resolve) => {
        let settled = false;

        const finish = (isReady) => {
          if (settled) return;
          settled = true;
          poster.removeEventListener('load', handleLoad);
          poster.removeEventListener('error', handleError);
          if (this.cancelPosterWait === cancel) {
            this.cancelPosterWait = null;
          }
          resolve(isReady);
        };

        const handleLoad = () => {
          this.decodePoster(poster).then(finish);
        };
        const handleError = () => finish(false);
        const cancel = () => finish(false);

        this.cancelPosterWait = cancel;
        poster.addEventListener('load', handleLoad, { once: true });
        poster.addEventListener('error', handleError, { once: true });

        if (poster.complete) {
          this.decodePoster(poster).then(finish);
        }
      });
    }

    mountPoster(slide) {
      if (!slide) return null;

      const mount = slide.querySelector('[data-kiwibit-poster-mount]');
      const template = slide.querySelector('template[data-kiwibit-poster-template]');
      if (mount && !mount.querySelector('[data-kiwibit-poster]') && template?.content) {
        mount.appendChild(template.content.cloneNode(true));
      }
      const poster = mount?.querySelector('[data-kiwibit-poster] img') || null;
      if (poster) {
        poster.loading = 'eager';
        if (!slide.classList.contains('is-active')) {
          poster.fetchPriority = 'auto';
        }
      }
      return poster;
    }

    async decodePoster(poster) {
      if (!poster?.complete || poster.naturalWidth <= 0) return false;
      if (typeof poster.decode !== 'function') return true;

      try {
        await poster.decode();
      } catch (_) {
        return poster.complete && poster.naturalWidth > 0;
      }
      return true;
    }

    playVideoWithTimeout(video, timeoutMs) {
      let timeoutId = 0;
      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = window.setTimeout(() => reject(new Error('Video playback timed out')), timeoutMs);
      });
      return Promise.race([Promise.resolve(video.play()), timeoutPromise]).finally(() => {
        window.clearTimeout(timeoutId);
      });
    }

    attachActiveVideoState(video, slide, playbackGeneration) {
      const hideVideo = () => {
        if (!this.isPlaybackCurrent(playbackGeneration, slide) || this.activeVideo !== video) return;
        slide.classList.remove('is-media-ready');
      };
      const revealVideo = () => {
        if (!this.isPlaybackCurrent(playbackGeneration, slide) || this.activeVideo !== video) return;
        this.revealVideoAfterFrame(video, slide, playbackGeneration);
      };
      const failVideo = () => {
        if (!this.isPlaybackCurrent(playbackGeneration, slide) || this.activeVideo !== video) return;
        this.teardownActiveVideo();
        this.mediaMode = 'image';
        if (this.slides.length > 1) {
          this.startImageTimer(playbackGeneration);
        }
      };
      let stalledTimeout = 0;
      const handleStalled = () => {
        hideVideo();
        window.clearTimeout(stalledTimeout);
        stalledTimeout = window.setTimeout(failVideo, 10000);
      };
      const handlePlaying = () => {
        window.clearTimeout(stalledTimeout);
        revealVideo();
      };

      video.addEventListener('waiting', handleStalled);
      video.addEventListener('stalled', handleStalled);
      video.addEventListener('playing', handlePlaying);
      video.addEventListener('error', failVideo);
      this.activeVideoCleanup = () => {
        window.clearTimeout(stalledTimeout);
        video.removeEventListener('waiting', handleStalled);
        video.removeEventListener('stalled', handleStalled);
        video.removeEventListener('playing', handlePlaying);
        video.removeEventListener('error', failVideo);
      };
    }

    revealVideoAfterFrame(video, slide, playbackGeneration) {
      this.cancelVideoFrameWait?.();

      let frameCallbackId = 0;
      let fallbackFrameId = 0;
      let timeoutId = 0;
      let settled = false;
      const finish = (isReady) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        if (frameCallbackId && typeof video.cancelVideoFrameCallback === 'function') {
          video.cancelVideoFrameCallback(frameCallbackId);
        }
        if (fallbackFrameId) window.cancelAnimationFrame(fallbackFrameId);
        if (this.cancelVideoFrameWait === cancel) {
          this.cancelVideoFrameWait = null;
        }
        if (
          isReady
          && this.isPlaybackCurrent(playbackGeneration, slide)
          && this.activeVideo === video
          && !video.paused
        ) {
          slide.classList.add('is-media-ready');
        }
      };
      const cancel = () => finish(false);

      this.cancelVideoFrameWait = cancel;
      timeoutId = window.setTimeout(() => finish(video.readyState >= 2), 8000);
      if (typeof video.requestVideoFrameCallback === 'function') {
        frameCallbackId = video.requestVideoFrameCallback(() => finish(true));
      } else {
        fallbackFrameId = window.requestAnimationFrame(() => {
          fallbackFrameId = window.requestAnimationFrame(() => finish(video.readyState >= 2));
        });
      }
    }

    getActiveVideoTemplate(slide) {
      if (!slide) return null;

      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const preferredMode = isMobile ? 'mobile' : 'desktop';
      const preferredTemplate = slide.querySelector(`[data-kiwibit-video-template="${preferredMode}"]`);

      if (preferredTemplate || !isMobile) return preferredTemplate;
      return slide.querySelector('[data-kiwibit-video-template="desktop"]');
    }

    mountVideo(slide, template) {
      const mount = slide?.querySelector('[data-kiwibit-video-mount]');
      const templateVideo = template?.content?.querySelector('video');
      if (!mount || !templateVideo) return null;

      const video = templateVideo.cloneNode(true);
      video.autoplay = false;
      video.controls = false;
      video.muted = true;
      video.playsInline = true;
      video.preload = 'auto';
      video.removeAttribute('poster');
      video.setAttribute('muted', 'muted');
      video.setAttribute('playsinline', 'playsinline');
      video.querySelectorAll('img').forEach((image) => image.remove());

      mount.replaceChildren(video);
      this.activeVideo = video;
      this.activeVideoMount = mount;
      video.load();
      return video;
    }

    teardownActiveVideo() {
      const video = this.activeVideo;
      this.cancelVideoFrameWait?.();
      this.cancelVideoFrameWait = null;
      this.activeVideoCleanup?.();
      this.activeVideoCleanup = null;
      if (video) {
        video.onended = null;
        video.pause();
        try {
          video.currentTime = 0;
        } catch {}
      }

      this.slides?.forEach((slide) => slide.classList.remove('is-media-ready'));
      this.activeVideoMount?.replaceChildren();
      this.activeVideo = null;
      this.activeVideoMount = null;
    }

    isPlaybackCurrent(playbackGeneration, slide) {
      return (
        playbackGeneration === this.playbackGeneration
        && this.slides[this.activeIndex] === slide
        && slide?.classList.contains('is-active')
        && !document.hidden
        && this.isInViewport
      );
    }
  }

  customElements.define('kiwibit-slideshow', KiwibitSlideshow);
}
